import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from '@notifications/services/notifications.service';
import { PlatformService } from '@panamax/app-state';
import {
  extractPoDates,
  isCurrentDate,
} from '@product-detail/utils/po-dates-util';
import { OrderItem } from '@usf/ngrx-order';
import { ProductSummary } from '@usf/product-types/Product-Summary';
import { BehaviorSubject, map, Observable, take } from 'rxjs';
import { ImageVariantEnum } from 'src/app/product-detail/models/image-variant.enum';
import {
  getAlternativeThumbnail,
  getFirstImageURL,
} from 'src/app/product-detail/utils/product-images.util';
import { NavigationHelperService } from 'src/app/shared/helpers/navigation.helpers.service';
import { CutProduct } from '../../models/warehouse-cuts-models';

interface OrderedProductUnavailableItemCardViewModel {
  cutProduct: CutProduct;
  thumbnailImageURL?: string;
  expectedInStock?: string;
  maxDisplayTags?: number;
}

@Component({
  selector: 'app-ordered-product-unavailable-item-card',
  templateUrl: './ordered-product-unavailable-item-card.component.html',
  styleUrls: ['./ordered-product-unavailable-item-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderedProductUnavailableItemCardComponent implements OnInit {
  @Output() productSelect: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input()
  get cutProduct(): CutProduct {
    return this._cutProductSubject.getValue();
  }

  set cutProduct(value: CutProduct) {
    this._cutProductSubject.next(value);
  }
  // View model observable that the template will subscribe to
  public viewModel$: Observable<OrderedProductUnavailableItemCardViewModel>;

  private _cutProductSubject = new BehaviorSubject<CutProduct>(null);

  constructor(
    readonly platformService: PlatformService,
    private readonly navHelper: NavigationHelperService,
    private platform: Platform,
    private translateService: TranslateService,
    readonly notificationsService: NotificationsService,
  ) {}

  get productSummary(): ProductSummary {
    return this.cutProduct?.original?.product?.summary;
  }

  get isInStock(): boolean {
    return this.cutProduct?.original?.product?.inventory?.isInStock;
  }

  get processed(): boolean {
    return this.cutProduct?.original?.processed;
  }

  get lastItem(): boolean {
    return false;
  }

  get shouldHideSubbedItem(): boolean {
    // Only hide if this is an explicitly marked substituted item
    return false;
  }

  get item(): OrderItem {
    return this.cutProduct?.original?.orderItem;
  }

  get lastUpdatedBy(): string | undefined {
    return this.cutProduct?.original?.lastUpdatedBy;
  }

  get orderNumber(): number | undefined {
    return this.cutProduct?.original?.orderNumber;
  }

  ngOnInit(): void {
    this.viewModel$ = this._cutProductSubject.pipe(
      map(cutProduct => {
        const newViewModel: OrderedProductUnavailableItemCardViewModel = {
          cutProduct: cutProduct,
          maxDisplayTags:
            this.platform?.is('mobile') && !this.platform?.is('tablet') ? 2 : 5,
        };

        // Only add these properties if cutProduct exists
        if (cutProduct) {
          newViewModel.thumbnailImageURL = getFirstImageURL(
            this.productSummary,
            ImageVariantEnum.Thumbnail,
          );
          newViewModel.expectedInStock = this.getExpectedInStockText();
        }
        return newViewModel;
      }),
    );
  }

  productClickHandler() {
    this.navHelper?.routeToProductDetails(this.productSummary.productNumber);
  }

  orderClickHandler() {
    this.notificationsService
      ?.getCart()
      ?.pipe(take(1))
      ?.subscribe(cart => {
        if (
          cart?.orderItems?.length > 0 &&
          cart?.orderItems?.find(
            item => item?.productNumber === this.productSummary.productNumber,
          )
        ) {
          this.navHelper?.routeTo('order');
        } else {
          this.navHelper?.routeTo('my-orders-view');
        }
      });
  }

  private getExpectedInStockText(): string {
    if (!this.productSummary || !this.productSummary.poDates) {
      return this.translateService.instant(
        'i18n.notifications.warehouseCuts.expectedUnavailable',
      );
    }

    if (this.isInStock) {
      return '';
    }

    const poDates = extractPoDates(this.productSummary.poDates);

    if (!poDates?.length) {
      return this.translateService.instant(
        'i18n.notifications.warehouseCuts.expectedUnavailable',
      );
    }

    const isExpectedToday = isCurrentDate(poDates[0]);

    if (poDates[0]?.scheduledArrivalDate) {
      return isExpectedToday
        ? this.translateService.instant(
            'i18n.orderSubmittedPage.expectedInStockToday',
          )
        : this.translateService.instant(
            'i18n.notifications.warehouseCuts.expectedInStock',
          ) + poDates[0]?.formattedArrivalDate;
    } else {
      return this.translateService.instant(
        'i18n.notifications.warehouseCuts.expectedUnavailable',
      );
    }
  }

  selectProduct(): void {
    this.productSelect.emit(true);
  }

  getAternativeThumbnail = (thumbnailImageURL: string): string =>
    getAlternativeThumbnail(this.productSummary, thumbnailImageURL);
}
